import { useCallback } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Field,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  TypographyWeights,
  useFormApi,
} from '@carboledger/ui';
import { generateReferenceId } from '@/lib/utils';
import { useTranslations } from '@/hooks';
import styles from './GeneratedIdField.module.scss';

/**
 * Props:
 *   name         form field name for the id box
 *   label        the field label — always required, so it carries the asterisk
 *   placeholder  the empty-input hint
 *   readOnly     a saved site keeps its id: locks the box and Generate
 */
export const GeneratedIdField = ({ name, label, placeholder, readOnly = false }) => {
  const t = useTranslations();
  const { setValue } = useFormApi();

  const handleGenerate = useCallback(() => {
    setValue(name, generateReferenceId());
  }, [name, setValue]);

  return (
    <div className={styles.field}>
      <Typography
        variant={TypographyVariants.bodyXs}
        weight={TypographyWeights.semibold}
        color={TypographyColors.secondary}
      >
        {label}
        <span className={styles.required} aria-hidden="true">
          {' '}
          *
        </span>
      </Typography>

      <div className={styles.controls}>
        <Field name={name} required={!readOnly}>
          {({ id, name: inputName, value, onChange, onBlur, status, message }) => (
            <TextInput
              className={styles.input}
              id={id}
              name={inputName}
              aria-label={label}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              disabled={readOnly}
            />
          )}
        </Field>

        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.md}
          onClick={handleGenerate}
          disabled={readOnly}
        >
          {t('FORM_GENERATE')}
        </Button>
      </div>
    </div>
  );
};
